import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-2" }
const _hoisted_3 = { class: "text-xs font-medium uppercase tracking-wider text-gray-500" }
const _hoisted_4 = {
  key: 0,
  class: "ml-1.5 text-gray-400"
}
const _hoisted_5 = { class: "min-h-0 flex-1 overflow-y-auto px-4 pb-4" }
const _hoisted_6 = {
  key: 1,
  class: "mt-4 rounded border-l-4 border border-[rgba(var(--v-theme-error),0.3)] bg-[rgba(var(--v-theme-error),0.08)] px-3.5 py-3 text-xs",
  "data-cy": "conflict-preview-error"
}
const _hoisted_7 = {
  class: "border-b border-gray-200 py-4",
  "data-cy": "conflict-preview-record-box"
}
const _hoisted_8 = { class: "mb-2.5 flex items-baseline justify-between gap-3" }
const _hoisted_9 = { class: "text-xs font-medium uppercase tracking-wider text-gray-500" }
const _hoisted_10 = ["href"]
const _hoisted_11 = { class: "text-gray-500" }
const _hoisted_12 = { class: "m-0 [overflow-wrap:anywhere]" }
const _hoisted_13 = { key: 0 }
const _hoisted_14 = {
  key: 1,
  class: "text-gray-400"
}
const _hoisted_15 = {
  key: 0,
  class: "border-b border-gray-200 py-4",
  "data-cy": "conflict-preview-client-box"
}
const _hoisted_16 = { class: "mb-2.5 flex items-baseline justify-between gap-3" }
const _hoisted_17 = ["href"]
const _hoisted_18 = { class: "text-gray-500" }
const _hoisted_19 = { class: "m-0 [overflow-wrap:anywhere]" }
const _hoisted_20 = { key: 0 }
const _hoisted_21 = {
  key: 1,
  class: "text-gray-400"
}
const _hoisted_22 = { class: "py-4" }
const _hoisted_23 = { class: "text-gray-500" }
const _hoisted_24 = { class: "m-0 leading-relaxed [overflow-wrap:anywhere]" }
const _hoisted_25 = {
  key: 0,
  class: "italic text-gray-400",
  "data-cy": "conflict-preview-unavailable"
}
const _hoisted_26 = {
  key: 1,
  class: "text-gray-400"
}
const _hoisted_27 = {
  key: 0,
  class: "bg-yellow-200 px-0.5 text-yellow-900"
}
const _hoisted_28 = { class: "mt-3 text-xs leading-relaxed text-gray-500" }
const _hoisted_29 = { class: "font-medium text-gray-700" }

import { computed } from 'vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import { highlightTokens } from '../utils/conflictHighlightHelpers';
import { conflictFieldLabel } from '../constants/conflictFieldConstants';
import type {
  ConflictPreviewField,
  ConflictPreviewRecord,
} from '../models/conflictTypes';


export default /*@__PURE__*/_defineComponent({
  __name: 'ConflictPreviewPanel',
  props: {
    record: {},
    loading: { type: Boolean },
    error: {},
    includedTimeEntries: { type: Boolean },
    clientHref: {},
    matterHref: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

/** Inline, not Tailwind arbitrary values — see the comment on ConflictCheckPage. */
const SUMMARY_COLUMNS = { gridTemplateColumns: 'auto minmax(0, 1fr)' };
const FIELD_COLUMNS = {
  gridTemplateColumns: 'minmax(110px, auto) minmax(0, 1fr)',
};

const KIND_LABELS: Record<string, string> = {
  client: 'Client',
  matter: 'Matter',
  timeEntry: 'Time entry',
};

const kindLabel = computed(() =>
  props.record ? KIND_LABELS[props.record.type] ?? 'Record' : 'Record',
);

/** The record's own link: a matter opens the matter, a client opens the client. */
const recordHref = computed(() =>
  props.record?.type === 'matter' ? props.matterHref : props.clientHref,
);

const fieldLabel = (field: ConflictPreviewField) => conflictFieldLabel(field.id);

function tokensFor(field: ConflictPreviewField) {
  return highlightTokens(field.value, field.matchedWords);
}

return (_ctx: any,_cache: any) => {
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!

  return (_openBlock(), _createElementBlock("aside", {
    class: "flex min-w-0 flex-col border-l border-gray-200 bg-white",
    role: "dialog",
    "aria-label": __props.record?.label || 'Result preview',
    "data-cy": "conflict-preview-panel"
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("span", _hoisted_3, [
        _createTextVNode(_toDisplayString(kindLabel.value) + " ", 1),
        (__props.record?.archived)
          ? (_openBlock(), _createElementBlock("span", _hoisted_4, "· Archived"))
          : _createCommentVNode("", true)
      ]),
      _createVNode(LlSecondaryButton, {
        icon: "mdi-close",
        variant: "text",
        size: "small",
        "aria-label": "Close preview",
        "data-cy": "conflict-preview-close",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close')))
      })
    ]),
    _createElementVNode("div", _hoisted_5, [
      (__props.loading)
        ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
            key: 0,
            type: "paragraph, list-item-two-line@4"
          }))
        : (__props.error)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [...(_cache[1] || (_cache[1] = [
              _createElementVNode("span", { class: "font-medium text-red-700" }, "This record could not be loaded.", -1),
              _createTextVNode(" The result above still stands — only the detail below is missing. ", -1)
            ]))]))
          : (__props.record)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                _createElementVNode("section", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("h3", _hoisted_9, _toDisplayString(kindLabel.value), 1),
                    (recordHref.value)
                      ? (_openBlock(), _createElementBlock("a", {
                          key: 0,
                          href: recordHref.value,
                          target: "_blank",
                          rel: "noopener noreferrer",
                          class: "shrink-0 text-xs text-blue-700 underline underline-offset-2 hover:text-blue-900",
                          "data-cy": "conflict-preview-open-record"
                        }, " Open " + _toDisplayString(kindLabel.value), 9, _hoisted_10))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("dl", {
                    class: "grid gap-x-3.5 gap-y-1.5 text-sm",
                    style: SUMMARY_COLUMNS
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.record.summary, (item) => {
                      return (_openBlock(), _createElementBlock(_Fragment, {
                        key: item.label
                      }, [
                        _createElementVNode("dt", _hoisted_11, _toDisplayString(item.label), 1),
                        _createElementVNode("dd", _hoisted_12, [
                          (item.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(item.value), 1))
                            : (_openBlock(), _createElementBlock("span", _hoisted_14, "—"))
                        ])
                      ], 64))
                    }), 128))
                  ])
                ]),
                (__props.record.client)
                  ? (_openBlock(), _createElementBlock("section", _hoisted_15, [
                      _createElementVNode("div", _hoisted_16, [
                        _cache[2] || (_cache[2] = _createElementVNode("h3", { class: "text-xs font-medium uppercase tracking-wider text-gray-500" }, " Client ", -1)),
                        (__props.clientHref)
                          ? (_openBlock(), _createElementBlock("a", {
                              key: 0,
                              href: __props.clientHref,
                              target: "_blank",
                              rel: "noopener noreferrer",
                              class: "shrink-0 text-xs text-blue-700 underline underline-offset-2 hover:text-blue-900",
                              "data-cy": "conflict-preview-open-client"
                            }, " Open Client ", 8, _hoisted_17))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("dl", {
                        class: "grid gap-x-3.5 gap-y-1.5 text-sm",
                        style: SUMMARY_COLUMNS
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.record.client.details, (item) => {
                          return (_openBlock(), _createElementBlock(_Fragment, {
                            key: item.label
                          }, [
                            _createElementVNode("dt", _hoisted_18, _toDisplayString(item.label), 1),
                            _createElementVNode("dd", _hoisted_19, [
                              (item.value)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_20, _toDisplayString(item.value), 1))
                                : (_openBlock(), _createElementBlock("span", _hoisted_21, "—"))
                            ])
                          ], 64))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("section", _hoisted_22, [
                  _cache[5] || (_cache[5] = _createElementVNode("h3", { class: "mb-2 text-xs font-medium uppercase tracking-wider text-gray-500" }, " Every field this check searched ", -1)),
                  _cache[6] || (_cache[6] = _createElementVNode("p", { class: "mb-2.5 text-xs leading-relaxed text-gray-500" }, " A field with no value was still read. ", -1)),
                  _createElementVNode("dl", {
                    class: "grid items-baseline gap-x-3.5 gap-y-1.5 text-sm",
                    style: FIELD_COLUMNS,
                    "data-cy": "conflict-preview-fields"
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.record.searchedFields, (field) => {
                      return (_openBlock(), _createElementBlock(_Fragment, {
                        key: field.label
                      }, [
                        _createElementVNode("dt", _hoisted_23, _toDisplayString(fieldLabel(field)), 1),
                        _createElementVNode("dd", _hoisted_24, [
                          (field.unavailable)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_25, " searched, but not returned by this record "))
                            : (!field.value)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_26, "—"))
                              : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(tokensFor(field), (token, index) => {
                                  return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                                    (token.matched)
                                      ? (_openBlock(), _createElementBlock("mark", _hoisted_27, _toDisplayString(token.text), 1))
                                      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                          _createTextVNode(_toDisplayString(token.text), 1)
                                        ], 64))
                                  ], 64))
                                }), 128))
                        ])
                      ], 64))
                    }), 128))
                  ]),
                  _createElementVNode("p", _hoisted_28, [
                    _cache[3] || (_cache[3] = _createTextVNode(" Time entry descriptions on this record: ", -1)),
                    _createElementVNode("span", _hoisted_29, _toDisplayString(__props.includedTimeEntries ? 'searched' : 'not searched'), 1),
                    _cache[4] || (_cache[4] = _createTextVNode(". Invoice narratives are never searched. ", -1))
                  ])
                ])
              ], 64))
            : _createCommentVNode("", true)
    ])
  ], 8, _hoisted_1))
}
}

})